import React from 'react';
import { motion } from 'framer-motion';
import { School, Users, Rocket, Star, Zap } from 'lucide-react';

const HIGHLIGHTS = [
  {
    Icon: School,
    color: 'text-brand-indigo',
    bg: 'bg-brand-indigo/10',
    accent: 'border-t-brand-indigo',
    accentBg: 'bg-brand-indigo',
    title: 'Advanced Learning Initiative',
    partner: 'Educational Excellence Partners',
    desc: 'Empowering talented students from underserved communities to access advanced academic programmes and specialised courses.',
  },
  {
    Icon: Users,
    color: 'text-brand-emerald',
    bg: 'bg-brand-emerald/10',
    accent: 'border-t-brand-emerald',
    accentBg: 'bg-brand-emerald',
    title: 'Skills Development Program',
    partner: 'Career Advancement Alliance',
    desc: 'Providing comprehensive skill training and certification programmes to prepare young adults for meaningful employment.',
  },
  {
    Icon: Rocket,
    color: 'text-brand-amber',
    bg: 'bg-brand-amber/10',
    accent: 'border-t-brand-amber',
    accentBg: 'bg-brand-amber',
    title: 'STEM Excellence Fund',
    partner: 'Innovation Catalyst Network',
    desc: 'Supporting academically gifted students pursuing science, technology, engineering, and mathematics disciplines.',
  },
];

const cardVar = {
  hidden:  { opacity: 0, y: 28 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.13, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55 } } };

const SponsorshipHighlightsSection = () => (
  <section id="sponsorship-highlights" className="py-20 md:py-28 bg-muted/25">
    <div className="container mx-auto px-4 sm:px-6">

      {/* Badge + Header */}
      <motion.div
        className="text-center mb-16"
        initial="hidden"
        whileInView="visible"
        variants={fadeUp}
        viewport={{ once: true }}
      >
        <div className="flex justify-center mb-4">
          <span className="section-badge-amber">
            <Zap className="h-3 w-3" />
            Upcoming Initiatives
          </span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold mb-5">
          Future <span className="gradient-text">Spotlights</span>
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          Showcasing upcoming partnership programmes and the transformative educational
          opportunities they will create for deserving students.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-5">
        {HIGHLIGHTS.map(({ Icon, color, bg, accentBg, title, partner, desc }, i) => (
          <motion.div
            key={title}
            custom={i}
            variants={cardVar}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            whileHover={{ y: -7, scale: 1.015 }}
            transition={{ type: 'spring', stiffness: 300, damping: 22 }}
            className="group glassmorphism-card border border-border/50 rounded-2xl p-7
                       hover:shadow-xl hover:border-primary/15 transition-all duration-300
                       relative overflow-hidden flex flex-col"
          >
            {/* Top accent bar */}
            <div className={`absolute top-0 left-0 right-0 h-[3px] ${accentBg} rounded-t-2xl
                             opacity-60 group-hover:opacity-100 transition-opacity duration-300`} />

            {/* Header row */}
            <div className="flex items-center justify-between mb-5">
              <div className={`p-3.5 rounded-2xl ${bg} group-hover:scale-110 transition-transform duration-300`}>
                <Icon className={`h-6 w-6 ${color}`} />
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-amber/10
                               border border-brand-amber/20">
                <Star className="h-3 w-3 text-brand-amber fill-current" />
                <span className="text-xs text-brand-amber font-bold">Planned</span>
              </div>
            </div>

            {/* Content */}
            <h3 className="text-[15px] font-bold text-foreground mb-1.5 leading-snug tracking-tight">{title}</h3>
            <p className={`text-sm font-semibold ${color} mb-4`}>{partner}</p>
            <p className="text-sm text-muted-foreground leading-relaxed flex-1">{desc}</p>

            {/* Hover glow */}
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500
                            pointer-events-none"
                 style={{ background: 'radial-gradient(circle at 50% 0%, hsl(var(--primary)/0.04) 0%, transparent 60%)' }} />
          </motion.div>
        ))}
      </div>

      <motion.p
        className="text-center mt-12 text-xs text-muted-foreground/60 italic"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.6 }}
      >
        * These showcase upcoming programme initiatives. Real partnership stories will be
        highlighted as our programmes develop.
      </motion.p>
    </div>
  </section>
);

export default SponsorshipHighlightsSection;
